/**
 * Module rapports : génération à la demande, suivi, téléchargement, historique.
 *
 * Le public de chaque livrable est mirroré ici depuis `rapports/service.py` `PUBLIC_PAR_TYPE`, en
 * confort d'UX seulement — proposer un type de rapport qu'un profil ne peut pas demander mènerait
 * droit à un `403` (`docs/agent-spec/07-frontend-react.md` : le masquage frontend n'est jamais la
 * seule protection).
 */
import { useState } from "react";

import { useIdentite } from "../../shared/auth/contexte";
import type { Profil } from "../../shared/api/types";
import { ErreurApi } from "../../shared/api/erreurs";
import { Chargement, Erreur, Vide } from "../../shared/composants/Etats";
import { ouvrirTelechargement } from "../../shared/composants/telechargement";
import { formaterDate } from "../../shared/composants/formatage";
import {
  useDemanderRapport,
  useModelesDisponibles,
  useRapports,
  useSuiviRapport,
  useSupprimerRapport,
  useUrlDeTelechargementRapport,
} from "./requetes";

const LIVRABLES: { type: string; libelle: string; profils: readonly Profil[] }[] = [
  {
    type: "quotidien_hebdomadaire",
    libelle: "Rapport quotidien / hebdomadaire",
    profils: ["admin-totalenergies", "operationnel-totalenergies", "air-tahiti"],
  },
  {
    type: "export_operationnel",
    libelle: "Export opérationnel",
    profils: ["admin-totalenergies", "operationnel-totalenergies"],
  },
  {
    type: "export_client",
    libelle: "Export client",
    profils: ["admin-totalenergies", "operationnel-totalenergies", "air-tahiti"],
  },
  {
    type: "tableau_de_bord_mensuel",
    libelle: "Tableau de bord mensuel",
    profils: ["admin-totalenergies", "operationnel-totalenergies"],
  },
  {
    type: "rapport_maintenance",
    libelle: "Rapport de maintenance",
    profils: ["admin-totalenergies", "operationnel-totalenergies"],
  },
  {
    type: "rapport_documentaire",
    libelle: "Rapport documentaire",
    profils: ["admin-totalenergies", "operationnel-totalenergies"],
  },
];

const LIBELLE_STATUT: Record<string, string> = {
  en_cours: "en cours de génération",
  pret: "prêt",
  echec: "échec",
};

export function EcranRapports() {
  const identite = useIdentite();
  const disponibles = LIVRABLES.filter((l) => l.profils.includes(identite.profil));

  const [type, definirType] = useState(disponibles[0]?.type ?? "");
  const [modeleId, definirModeleId] = useState("");
  const [rapportEnSuivi, definirSuivi] = useState<string | null>(null);
  const [aSupprimer, definirASupprimer] = useState<string | null>(null);

  const demander = useDemanderRapport();
  const modeles = useModelesDisponibles(type);
  const suivi = useSuiviRapport(rapportEnSuivi);
  const historique = useRapports();
  const telechargement = useUrlDeTelechargementRapport();
  const supprimer = useSupprimerRapport();

  const libelleDe = (type: string) => LIVRABLES.find((l) => l.type === type)?.libelle ?? type;
  const modelesDuLivrable = modeles.data?.data ?? [];
  // Tant que l'utilisateur n'a rien choisi explicitement, le premier modèle de la liste (le plus
  // souvent le modèle par défaut du livrable) fait foi — le sélecteur ne doit jamais afficher un
  // modèle sans que la soumission envoie ce même modèle.
  const modeleEffectif = modeleId || modelesDuLivrable[0]?.modele_id || "";

  function changerLivrable(nouveauType: string) {
    definirType(nouveauType);
    definirModeleId("");
    // Efface immédiatement le panneau de suivi précédent : sans cela, le statut et le bouton
    // « Télécharger » d'une génération antérieure restent affichés tels quels, ce qui laisse
    // croire que changer de livrable n'a aucun effet tant qu'on n'a pas cliqué sur « Générer ».
    definirSuivi(null);
  }

  async function demanderUnRapport(evenement: React.FormEvent) {
    evenement.preventDefault();
    // `format` par défaut du serveur, mais explicite ici : le type généré marque ce champ comme
    // toujours présent (il porte un défaut dans le schéma).
    const reponse = await demander.mutateAsync({
      type,
      corps: { format: "csv", modele_id: modeleEffectif || undefined },
    });
    definirSuivi(reponse.data.rapport_id);
  }

  function telecharger(rapportId: string): void {
    ouvrirTelechargement(async () => (await telechargement.mutateAsync(rapportId)).data.url_telechargement);
  }

  async function confirmerSuppression(rapportId: string) {
    await supprimer.mutateAsync(rapportId);
    definirASupprimer(null);
    if (rapportEnSuivi === rapportId) definirSuivi(null);
  }

  return (
    <section>
      <h1>Rapports</h1>

      <form className="formulaire-ligne" onSubmit={(e) => void demanderUnRapport(e)}>
        <label htmlFor="type-rapport">Livrable</label>
        <select id="type-rapport" value={type} onChange={(e) => changerLivrable(e.target.value)}>
          {disponibles.map((livrable) => (
            <option key={livrable.type} value={livrable.type}>
              {livrable.libelle}
            </option>
          ))}
        </select>
        {modelesDuLivrable.length > 1 && (
          <>
            <label htmlFor="modele-rapport">Modèle</label>
            <select
              id="modele-rapport"
              value={modeleEffectif}
              onChange={(e) => definirModeleId(e.target.value)}
            >
              {modelesDuLivrable.map((modele) => (
                <option key={modele.modele_id} value={modele.modele_id}>
                  {modele.libelle}
                </option>
              ))}
            </select>
          </>
        )}
        <button type="submit" disabled={demander.isPending || !type}>
          Générer
        </button>
      </form>

      {rapportEnSuivi && suivi.data && (
        <p className="rapports__suivi" role="status">
          {libelleDe(suivi.data.data.type_rapport)} — Statut :{" "}
          {LIBELLE_STATUT[suivi.data.data.statut] ?? suivi.data.data.statut}
          {suivi.data.data.statut === "pret" && (
            <button type="button" onClick={() => telecharger(rapportEnSuivi)}>
              Télécharger
            </button>
          )}
          {suivi.data.data.statut === "echec" && suivi.data.data.message_erreur && (
            <span className="rapports__erreur"> — {suivi.data.data.message_erreur}</span>
          )}
        </p>
      )}

      <h2>Historique</h2>
      {historique.isPending && <Chargement libelle="Chargement de l'historique…" />}
      {historique.isError && (
        <Erreur erreur={historique.error} reessayer={() => void historique.refetch()} />
      )}
      {historique.data && historique.data.data.length === 0 && (
        <Vide libelle="Aucune demande de rapport pour l'instant." />
      )}
      {historique.data && historique.data.data.length > 0 && (
        <table className="tableau">
          <thead>
            <tr>
              <th scope="col">Livrable</th>
              <th scope="col">Demandé le</th>
              <th scope="col">Statut</th>
              <th scope="col"></th>
            </tr>
          </thead>
          <tbody>
            {historique.data.data.map((rapport) => (
              <tr key={rapport.rapport_id}>
                <td>{libelleDe(rapport.type_rapport)}</td>
                <td>{formaterDate(rapport.date_demande)}</td>
                <td>{LIBELLE_STATUT[rapport.statut] ?? rapport.statut}</td>
                <td>
                  <div className="formulaire-actions">
                    {rapport.statut === "pret" && (
                      <button type="button" onClick={() => telecharger(rapport.rapport_id)}>
                        Télécharger
                      </button>
                    )}
                    {aSupprimer === rapport.rapport_id ? (
                      <>
                        <span>Supprimer ce rapport ?</span>
                        <button
                          type="button"
                          onClick={() => void confirmerSuppression(rapport.rapport_id)}
                          disabled={supprimer.isPending}
                        >
                          Confirmer
                        </button>
                        <button type="button" onClick={() => definirASupprimer(null)}>
                          Annuler
                        </button>
                      </>
                    ) : (
                      <button
                        type="button"
                        aria-label={`Supprimer le rapport ${libelleDe(rapport.type_rapport)} du ${formaterDate(rapport.date_demande)}`}
                        title="Supprimer"
                        onClick={() => definirASupprimer(rapport.rapport_id)}
                      >
                        🗑
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {supprimer.isError && (
        <p role="alert" className="formulaire-ligne__erreur">
          {supprimer.error instanceof ErreurApi ? supprimer.error.message : "La suppression a échoué."}
        </p>
      )}
    </section>
  );
}
